// FP3-035 — Read-only viewer for a completed (or in-progress) Assessment Run.
// FP3-035B — Articulation screener runs render the rich
// ArticulationScreenerResultPanel + Print/Display launchers.
import {
  ClipboardText,
  Star,
  Timer,
  Check,
  XCircle,
  Smiley,
  Printer,
  Eye,
} from "@phosphor-icons/react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import ArticulationScreenerResultPanel from "@/components/student/ArticulationScreenerResultPanel";

const TYPE_META = {
  skill_probe: { label: "Skill Probe", icon: ClipboardText },
  rubric_rating: { label: "Rubric / Rating", icon: Star },
  timed_trial: { label: "Timed Trial", icon: Timer },
  articulation_screener: { label: "Articulation Screener", icon: Smiley },
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

export default function AssessmentRunViewerDialog({ run, student, onClose, onRunUpdated }) {
  if (!run) return null;
  const meta = TYPE_META[run.assessment_type] || TYPE_META.skill_probe;
  const Icon = meta.icon;
  const s = run.summary || {};
  const isRubric = run.assessment_type === "rubric_rating";
  const isScreener = run.assessment_type === "articulation_screener";
  const isCompleted = run.status === "completed";

  const openPrint = () => {
    if (!student?.id || !run?.id) return;
    window.open(
      `/print/students/${student.id}/assessment-runs/${run.id}`,
      "_blank",
      "noopener,noreferrer",
    );
  };
  const openDisplay = () => {
    if (!student?.id || !run?.id) return;
    window.open(
      `/display/students/${student.id}/assessment-runs/${run.id}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <Dialog open={!!run} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid="assessment-run-viewer"
        aria-describedby={undefined}
        className="w-[95vw] sm:w-[90vw] sm:max-w-[1040px] max-h-[90vh] p-0 bg-white flex flex-col gap-0 overflow-hidden"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#ECEAE4] shrink-0 pr-14">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-[#1F2937] heading-font flex items-center gap-2 flex-wrap">
                <Icon size={18} weight="duotone" className="text-[#9C6B2E] shrink-0" />
                <span className="truncate">{run.title || "(untitled)"}</span>
                <Badge
                  data-testid={`assessment-run-viewer-status-${run.status}`}
                  className={`shrink-0 ml-1 text-[10px] ${
                    run.status === "completed"
                      ? "bg-[#E8F0EB] text-[#1E3F33]"
                      : "bg-[#FDF1E1] text-[#9C6B2E]"
                  }`}
                >
                  {run.status}
                </Badge>
              </DialogTitle>
              <p className="text-xs text-[#6B7280] mt-1">
                {meta.label} · {fmtDate(run.date_administered)}
                {run.skill_area ? ` · ${run.skill_area}` : ""}
                {isScreener && run.student_age_at_assessment?.label
                  ? ` · Age ${run.student_age_at_assessment.label}`
                  : ""}
              </p>
            </div>
            {isScreener && isCompleted && (
              // Right-side action buttons. The DialogContent ships
              // with an absolutely positioned close X at right-4
              // top-4; the surrounding header has pr-14 to reserve
              // that space so the X never lands on top of these.
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="assessment-run-viewer-display"
                  onClick={openDisplay}
                  className="h-8 text-xs"
                >
                  <Eye size={12} className="mr-1" /> Display
                </Button>
                <Button
                  size="sm"
                  data-testid="assessment-run-viewer-print"
                  onClick={openPrint}
                  className="h-8 text-xs bg-[#1E3F33] hover:bg-[#173127] text-white"
                >
                  <Printer size={12} weight="fill" className="mr-1" /> Print
                </Button>
              </div>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
          {isScreener && isCompleted ? (
            <ArticulationScreenerResultPanel
              run={run}
              student={student}
              onRunUpdated={onRunUpdated}
            />
          ) : (
            <>
              {/* Score summary (legacy / non-screener path) */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 rounded-xl border border-[#E5E3DB] bg-[#F9F8F6] p-3">
                <Stat label="Items" value={s.total_count ?? 0} />
                <Stat label="Scored" value={s.scored_count ?? 0} />
                {isRubric ? (
                  <Stat
                    label="Average rating"
                    value={s.average_rating == null ? "—" : `${s.average_rating}/5`}
                    accent
                  />
                ) : (
                  <>
                    <Stat
                      label="Correct"
                      value={
                        s.scored_count != null
                          ? `${s.correct_count ?? 0}/${s.scored_count}`
                          : "—"
                      }
                    />
                    <Stat
                      label="Accuracy"
                      value={s.percent_accuracy == null ? "—" : `${s.percent_accuracy}%`}
                      accent
                    />
                  </>
                )}
                {run.assessment_type === "timed_trial" && (
                  <Stat
                    label="Duration · Rate"
                    value={
                      run.timer_seconds
                        ? `${Math.floor(run.timer_seconds / 60)}:${(run.timer_seconds % 60).toString().padStart(2, "0")} · ${s.trials_per_minute ?? "—"} tpm`
                        : "Untimed"
                    }
                  />
                )}
              </div>

              {/* Items */}
              <div>
                <p className="text-[11px] uppercase tracking-wide font-semibold text-[#4B5563] mb-2">
                  {isRubric ? "Criteria" : "Items"}
                </p>
                <div className="space-y-1">
                  {(run.items || []).map((it, idx) => (
                    <div
                      key={it.id || idx}
                      data-testid={`assessment-run-viewer-item-${idx}`}
                      className="flex items-center gap-3 rounded-lg border border-[#F1F2F4] px-3 py-2"
                    >
                      <span className="text-[10px] font-mono text-[#9B9890] w-6 shrink-0">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <span className="flex-1 text-[13px] text-[#1F2937]">
                        {it.prompt || <span className="italic text-[#9B9890]">(no prompt)</span>}
                        {it.notes && (
                          <span className="block text-[11px] text-[#6B7280] mt-0.5 italic">
                            {it.notes}
                          </span>
                        )}
                      </span>
                      <ScoreCell item={it} isRubric={isRubric} />
                    </div>
                  ))}
                  {(!run.items || run.items.length === 0) && (
                    <p className="text-xs text-[#9B9890] italic">No items.</p>
                  )}
                </div>
              </div>

              {/* Narrative blocks */}
              {run.provider_notes && (
                <NarrativeBlock label="Provider notes" text={run.provider_notes} />
              )}
              {run.clinical_summary && (
                <NarrativeBlock label="Clinical / educational summary" text={run.clinical_summary} />
              )}
              {run.recommendations && (
                <NarrativeBlock label="Recommendations" text={run.recommendations} />
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, accent = false }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-medium">
        {label}
      </p>
      <p
        className={`text-base font-semibold tabular-nums ${
          accent ? "text-[#1E3F33]" : "text-[#1F2937]"
        }`}
      >
        {value ?? "—"}
      </p>
    </div>
  );
}

function NarrativeBlock({ label, text }) {
  return (
    <div className="space-y-1">
      <p className="text-[11px] uppercase tracking-wide font-semibold text-[#4B5563]">
        {label}
      </p>
      <p className="text-[13px] leading-relaxed text-[#1F2937] whitespace-pre-wrap rounded-lg border border-[#F1F2F4] bg-[#FBFAF6] px-3 py-2">
        {text}
      </p>
    </div>
  );
}

function ScoreCell({ item, isRubric }) {
  if (item.score == null) {
    return <span className="text-[11px] text-[#9B9890] italic">unscored</span>;
  }
  if (isRubric) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums text-[#1E3F33] bg-[#E8F0EB] px-2 py-0.5 rounded">
        {item.score} / 5
      </span>
    );
  }
  if (item.score === 1) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#1E3F33] bg-[#E8F0EB] px-2 py-0.5 rounded">
        <Check size={10} weight="bold" /> correct
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#7A3A2C] bg-[#F8E8E2] px-2 py-0.5 rounded">
      <XCircle size={10} weight="bold" /> incorrect
    </span>
  );
}
